import React, { useState } from 'react';
import { X, Archive, RotateCcw } from 'lucide-react';

interface RestoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestore: () => void;
  entityName: string;
  entityType: 'artist' | 'release' | 'contact';
}

export const RestoreModal: React.FC<RestoreModalProps> = ({
  isOpen,
  onClose,
  onRestore,
  entityName,
  entityType
}) => {
  const [isRestoring, setIsRestoring] = useState(false);

  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      await onRestore();
      onClose();
    } finally {
      setIsRestoring(false);
    }
  };

  const getEntityDisplayName = () => {
    switch (entityType) {
      case 'artist': return 'Artist';
      case 'release': return 'Release';
      case 'contact': return 'Contact';
      default: return 'Item';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-x-0 top-10 mx-auto w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Restore {getEntityDisplayName()}
            </h3>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Are you sure you want to restore <strong>{entityName}</strong>? 
            It will be moved back to the active {entityType}s list.
          </p>

          <div className="p-3 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-start gap-3">
              <Archive className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Restore {getEntityDisplayName()}
                </h4>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  This will make the {entityType} visible again in the main list.
                </p>
                <button
                  onClick={handleRestore}
                  disabled={isRestoring}
                  className="mt-2 px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRestoring ? 'Restoring...' : `Restore ${getEntityDisplayName()}`}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
